import { type JSX, useEffect, useState } from "react";

import CampaignsEmptyPlaceholder from "common/components/CampaignsEmptyPlaceholder";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useCampaigns from "common/datahooks/useCampaigns";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { NavTabsContentContainer } from "common/layouts/WorkspaceLayout";
import type { Campaign } from "common/types";

import { CAMPAIGNS_TAB_ITEMS } from "../../constants";
import useCampaignsFilters from "../../hooks/useCampaignsFilters";
import CreateCampaignButton from "./CreateCampaignButton";
import DesktopFilters from "./DesktopLayout/DesktopFilters";
import DesktopFilterTags from "./DesktopLayout/DesktopFilterTags";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ManageCampaigns from "./ManageCampaigns";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function CampaignsList() {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);

  const { campaigns, isLoadingCampaigns, campaignsError, refetchCampaigns } =
    useCampaigns();

  const {
    searchParams,
    setSearchParams,
    filteredCampaigns,
    hasSearchParams,
    searchText,
    setSearchText,
    clearSearchParams,
  } = useCampaignsFilters(campaigns);

  useEffect(() => {
    setSelectedCampaigns((prev) =>
      prev.filter(({ id }) =>
        filteredCampaigns.some(
          (filteredCampaign) => filteredCampaign.id === id,
        ),
      ),
    );
  }, [filteredCampaigns]);

  const manageCampaignsComponent = !!selectedCampaigns.length && (
    <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
      <ManageCampaigns
        selectedCampaigns={selectedCampaigns}
        setSelectedCampaigns={setSelectedCampaigns}
      />
    </RestrictedComponent>
  );

  const isEmpty = !isLoadingCampaigns && (!campaigns || !campaigns.length);

  let mainContent: JSX.Element;
  if (campaignsError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display Campaigns"
        onRetry={refetchCampaigns}
      />
    );
  } else if (isEmpty) {
    mainContent = <CampaignsEmptyPlaceholder />;
  } else {
    const Component = isDesktop ? DesktopTable : MobileList;

    const clearSearchParamsButton = (
      <RenderIf condition={hasSearchParams}>
        <Button
          variant="link-danger"
          size={isDesktop ? "sm" : "md"}
          onClick={clearSearchParams}
          className="lg:mt-1"
        >
          Clear all
        </Button>
      </RenderIf>
    );

    mainContent = (
      <>
        {!isDesktop && <CreateCampaignButton />}

        <div className="flex w-full items-center gap-2 lg:gap-2.5">
          <SearchInput
            className="max-lg:flex-1 lg:max-w-80"
            placeholder="Search campaigns"
            value={searchText}
            variant={isDesktop ? "sm" : "lg"}
            onChange={(event) => setSearchText(event.target.value)}
            onClear={() => setSearchText("")}
          />

          {isDesktop ? (
            <>
              <DesktopFilters
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />

              <div className="mr-auto" />

              {manageCampaignsComponent}
            </>
          ) : (
            <MobileFilters
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              clearSearchParamsButton={clearSearchParamsButton}
            />
          )}
        </div>

        {hasSearchParams && (
          <div className="flex items-start justify-between max-lg:hidden">
            <DesktopFilterTags
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
            {clearSearchParamsButton}
          </div>
        )}

        {filteredCampaigns.length || isLoadingCampaigns ? (
          <Component
            isLoading={isLoadingCampaigns}
            campaigns={filteredCampaigns}
            selectedCampaigns={selectedCampaigns}
            setSelectedCampaigns={setSelectedCampaigns}
          />
        ) : (
          <EmptyPlaceholder
            title="No results found"
            subtitle="There were no results that match your criteria"
            imageSrc={NoResultsImage}
            actionComponent={
              <Button
                onClick={() => {
                  setSearchText("");
                  clearSearchParams();
                }}
              >
                Clear Search
              </Button>
            }
          />
        )}
      </>
    );
  }

  return (
    <NavTabsContentContainer
      primaryActionsComponent={
        <RenderIf condition={!isEmpty}>
          {isDesktop ? <CreateCampaignButton /> : manageCampaignsComponent}
        </RenderIf>
      }
      tabItems={CAMPAIGNS_TAB_ITEMS}
    >
      {mainContent}
    </NavTabsContentContainer>
  );
}
